import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { Clock, Warning, CheckCircle, ChartBar } from "@phosphor-icons/react";

/**
 * Compact admin widget showing the current Acknowledge → Action SLA pulse:
 *   • Unactioned counts split by tier (pending / amber / critical)
 *   • Avg response time (last 30d) for jobs + requests
 * Wraps two columns (Jobs / Requests).
 */
export default function SLAResponseWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/sla/dashboard")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;

  const cells = [
    { key: "job_requests", title: "Requests", linkTo: "/job-requests" },
    { key: "jobs",         title: "Jobs",     linkTo: "/jobs" },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6 animate-fade-in" data-testid="sla-response-widget">
      {cells.map(({ key, title, linkTo }) => {
        const d = data[key] || {};
        const counts = d.by_state || { pending: 0, info_pinged: 0, amber: 0, critical: 0 };
        const totalUnactioned = d.total_unactioned || 0;
        const hasBreach = (counts.amber || 0) + (counts.critical || 0) > 0;
        return (
          <Link
            key={key}
            to={linkTo}
            className={`border rounded-lg p-4 bg-card hover:bg-muted/30 transition-colors block ${hasBreach ? "border-red-300 dark:border-red-800/60" : "border-border"}`}
            data-testid={`sla-widget-${key}`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold font-[Chivo] flex items-center gap-1.5">
                <ChartBar size={14} className="text-muted-foreground" />
                {title} • Action SLA
              </h3>
              <span className="text-[10px] text-muted-foreground tabular-nums">{totalUnactioned} unactioned</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded border border-border bg-muted/40 px-2 py-2 text-center">
                <p className="text-[9px] uppercase tracking-widest text-slate-600 dark:text-slate-400 flex items-center justify-center gap-0.5">
                  <Clock size={10} /> Pending
                </p>
                <p className="text-lg font-bold tabular-nums mt-0.5">{(counts.pending || 0) + (counts.info_pinged || 0)}</p>
              </div>
              <div className={`rounded border px-2 py-2 text-center ${counts.amber > 0 ? "border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-900/20" : "border-border bg-muted/40"}`}>
                <p className="text-[9px] uppercase tracking-widest text-amber-700 dark:text-amber-300 flex items-center justify-center gap-0.5">
                  <Warning size={10} /> Amber
                </p>
                <p className="text-lg font-bold tabular-nums mt-0.5 text-amber-700 dark:text-amber-300">{counts.amber || 0}</p>
              </div>
              <div className={`rounded border px-2 py-2 text-center ${counts.critical > 0 ? "border-red-300 bg-red-50 dark:border-red-800/60 dark:bg-red-900/20" : "border-border bg-muted/40"}`}>
                <p className="text-[9px] uppercase tracking-widest text-red-700 dark:text-red-300 flex items-center justify-center gap-0.5">
                  <Warning size={10} weight="fill" /> Critical
                </p>
                <p className="text-lg font-bold tabular-nums mt-0.5 text-red-700 dark:text-red-300">{counts.critical || 0}</p>
              </div>
            </div>
            <div className="mt-3 pt-2.5 border-t border-border flex items-center justify-between text-[11px]">
              <span className="text-muted-foreground flex items-center gap-1">
                <CheckCircle size={11} /> Avg response (30d)
              </span>
              <span className="font-semibold tabular-nums">
                {d.avg_response_label || "—"}
                {d.actioned_30d > 0 && (
                  <span className="text-muted-foreground font-normal ml-1.5">• {d.actioned_30d} actioned</span>
                )}
              </span>
            </div>
            {d.breach_pct_30d > 0 && (
              <p className="text-[10px] text-amber-700 dark:text-amber-400 mt-1">
                {d.breach_pct_30d}% of items pinged at amber/critical before being actioned
              </p>
            )}
          </Link>
        );
      })}
    </div>
  );
}
